import { AuthProviderProps } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import { AppSettingsService } from "@/service/AppSettingsService";
import Logger from "@/utils/Logger";
import { AuthService } from "@/service/AuthService";

export const createOidcConfig = (): AuthProviderProps => {
  /**
   * Handles the sign-in callback by restoring the user's intended navigation path
   * after a successful authentication.
   */
  const handleSigninCallback = () => {
    try {
      Logger.info("Handling sign-in callback", ["OidcConfig"]);

      // Consume and restore the saved redirect path
      const savedPath = AuthService.consumePostLoginRedirect();

      if (savedPath && savedPath !== "/") {
        Logger.info(`Restoring state to saved path: ${savedPath}`, ["OidcConfig"]);
        window.history.replaceState({}, document.title, savedPath);
      } else {
        Logger.info("No saved redirect path, staying on current page", ["OidcConfig"]);
      }
    } catch (error) {
      Logger.error("Error during sign-in callback", error, ["OidcConfig"]);
    }
  };

  /**
   * Handles the sign-out callback by logging the successful sign-out event.
   */
  const handleSignoutCallback = () => {
    Logger.info("User signed out successfully", ["OidcConfig"]);
    // Clear any saved redirect paths on sign-out
    sessionStorage.removeItem("postLoginRedirect");
  };

  const oidcConfig: AuthProviderProps = {
    // Core OIDC settings
    authority: AppSettingsService.getOpenIdAuthority(),
    client_id: AppSettingsService.getPingClientId(),
    response_type: "code",
    scope: "openid profile email",

    // Redirect URIs
    redirect_uri: `${window.location.origin}${window.location.pathname}`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    silent_redirect_uri: `${window.location.origin}/auth/silent-callback`,

    // Storage and session management
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    automaticSilentRenew: true,
    monitorSession: true,

    // Token and claim settings
    includeIdTokenInSilentRenew: true,
    filterProtocolClaims: true,
    loadUserInfo: true,

    // Event callbacks
    onSigninCallback: handleSigninCallback,
    onSignoutCallback: handleSignoutCallback,
  };

  Logger.info("OIDC Configuration initialized", ["OidcConfig"]);
  return oidcConfig;
};